import React, { useState } from 'react';
import { Language, SearchFilters } from '@/types';
import { ServiceOfferingForm } from '@/components/services/ServiceOfferingForm';
import { ServiceRequestList } from '@/components/services/ServiceRequestList';
import { IncomingRequests } from '@/components/exchanges/IncomingRequests';
import { ScheduledExchanges } from '@/components/exchanges/ScheduledExchanges';
import { ChatModal } from '@/components/chat/ChatModal';
import { SearchFilter } from '@/components/search/SearchFilter';
import { getTranslation } from '@/constants';
import { useServices } from '@/hooks';
import { useAuth } from '@/context/AuthContext';

interface HomePageProps {
  language: Language;
}

export const HomePage: React.FC<HomePageProps> = ({ language }) => {
  const { user } = useAuth();
  const { services, searchServices } = useServices(user?.uid);
  const t = getTranslation(language);

  const [activeTab, setActiveTab] = useState<'offer' | 'request' | 'scheduled' | 'incoming'>(
    'request'
  );
  const [chatExchangeId, setChatExchangeId] = useState<string | null>(null);

  const handleSearch = async (filters: SearchFilters) => {
    await searchServices(filters);
  };

  const tabs = [
    { key: 'request' as const, label: t.requestHelp },
    { key: 'offer' as const, label: t.offerServices },
    { key: 'scheduled' as const, label: t.myExchanges },
    { key: 'incoming' as const, label: t.incomingRequests },
  ];

  return (
    <div className="space-y-4">
      {/* Tabs - Mobile-friendly: compact, scrollable on small screens */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-1.5">
        <nav className="grid grid-cols-2 gap-1.5 sm:flex sm:gap-1" role="tablist">
          {tabs.map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              role="tab"
              aria-selected={activeTab === tab.key}
              className={`
                py-2 px-2 sm:px-3 rounded-lg font-medium text-xs sm:text-sm
                transition-all duration-200
                focus:outline-none focus:ring-2 focus:ring-blue-300
                min-h-[40px]
                ${activeTab === tab.key
                  ? 'bg-blue-600 text-white shadow-sm'
                  : 'bg-gray-50 text-gray-600 hover:bg-gray-100 hover:text-gray-800 border border-gray-200'
                }
              `.trim().replace(/\s+/g, ' ')}
            >
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab Content */}
      <div role="tabpanel" className="min-h-[300px]">
        {activeTab === 'offer' && <ServiceOfferingForm language={language} />}

        {activeTab === 'request' && (
          <div className="space-y-4">
            <SearchFilter language={language} onSearch={handleSearch} />
            <ServiceRequestList services={services} language={language} />
          </div>
        )}

        {activeTab === 'scheduled' && (
          <ScheduledExchanges language={language} onChatOpen={setChatExchangeId} />
        )}

        {activeTab === 'incoming' && (
          <IncomingRequests language={language} onChatOpen={setChatExchangeId} />
        )}
      </div>

      {/* Chat Modal */}
      <ChatModal
        isOpen={chatExchangeId !== null}
        onClose={() => setChatExchangeId(null)}
        exchangeId={chatExchangeId}
        language={language}
      />
    </div>
  );
};
